import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSignupExpertStore } from "@src/store/signupExpertStore"; // store
import MgInput from "../../../components/input/MgInput";
import { styled } from "styled-components";
import { Common } from "@src/styles/Common";

import { ExpertApplicationInfo } from "@src/api/expert/expertApi";
import SignupProfileUploadContainer from "../../../components/SignupExpert/Containers/SignupProfileUploader";
import ExpertSelect from "../../../components/SignupExpert/ExpertSelect";
import MgDialog from "../../../components/Dialog";
import { useJoinExpert, SignupExpertParams, CounselingCategory } from "@src/api/expert/expertApi";
import { registerExpertInfo, getUserPhone } from "@src/api/expert/expertApi";
import { transferToNative } from "@src/lib/WebAppBridge";

import missgoUnifiedLogo from "../../../assets/missgoUnifiedLogo.png";
import Cancel from "../../../assets/Signup/cancel";
import WaitConfirm from "../../../assets/Signup/waitConfirm.png";

const ExpertApplication = () => {
    const navigate = useNavigate();
    const { form, validationErrors, setPartialState } = useSignupExpertStore();
    const [loadingPhone, setLoadingPhone] = useState(true);

    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [applicationError, setApplicationError] = useState<string>("");
    const [canSubmit, setCanSubmit] = useState(false);

    const handleChange = (key: keyof SignupExpertParams, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setPartialState({
            form: {
                ...form,
                [key]: e.target.value,
            },
        });
    };

    const handlePendingPopupConfirm = () => {
        setShowPendingPopup(false);
        transferToNative("done", "closeWebView");
    };

    // career 객체 초기화 함수
    const initializeCareer = (categories: CounselingCategory[]): Record<CounselingCategory, number> => {
        const career: Record<CounselingCategory, number> = {
            중개: 0,
            매물컨설팅: 0,
            매수신청대리인: 0,
            임장동행: 0,
        };
        return career;
    };

    const validateAndSubmitForm = (runType: "validate" | "submit") => {
        const REQUIRED_KEYS: (keyof SignupExpertParams)[] = ["bankName", "bankAccount", "counselingCategories", "career"];

        const isEmptyValue = (key: keyof SignupExpertParams, value: any): boolean => {
            if (value == null) return true;
            if (key === "counselingCategories") return Array.isArray(value) && value.length === 0;
            if (key === "career") return !value || Object.keys(value).length === 0;
            if (typeof value === "string") return value.trim() === "";
            return false;
        };

        const missingKey = REQUIRED_KEYS.find((k) => isEmptyValue(k, form[k]));
        if (missingKey) {
            if (runType === "submit") {
                setApplicationError(`${KEY_TO_KR[missingKey]}`);
            } else {
                setCanSubmit(false);
            }
            return;
        }

        if (!form.profileFileUrl) {
            if (runType === "submit") setApplicationError("프로필 사진을 등록해주세요.");
            else setCanSubmit(false);
            return;
        }

        if (Object.keys(validationErrors).length > 0) {
            if (runType === "submit") setApplicationError("입력값을 확인해주세요.");
            else setCanSubmit(false);
            return;
        }

        const hasCert = !!form.auctionAgentCertFileUrl;
        const isDelegationCertUrlNeed = form.counselingCategories.includes("매수신청대리인") && !hasCert;
        if (isDelegationCertUrlNeed) {
            if (runType === "submit") setApplicationError("경매대행 선택 시 등록증을 업로드해주세요.");
            else setCanSubmit(false);
            return;
        }

        const isOfficeInfoNeed =
            (form.counselingCategories.includes("매수신청대리인") || form.counselingCategories.includes("중개")) &&
            !form.officeInfo;
        if (isOfficeInfoNeed) {
            if (runType === "submit") setApplicationError("중개사무소 정보를 입력해주세요.");
            else setCanSubmit(false);
            return;
        }

        const isCareerValid =
            form.counselingCategories.length > 0 &&
            (!form.career ||
                Object.keys(form.career).length !== form.counselingCategories.length ||
                Object.values(form.career).some((v) => v === 0));
        if (isCareerValid) {
            if (runType === "submit") setApplicationError("경력을 입력해주세요.");
            else setCanSubmit(false);
            return;
        }

        if (runType === "validate") {
            setCanSubmit(true);
            return;
        }

        if (runType === "submit") {
            doApply();
        }
    };

    const doApply = async () => {
        try {
            const accessToken = localStorage.getItem("accessToken"); // 이미 로그인 후 저장된 토큰
            const refreshToken = localStorage.getItem("refreshToken");
            if (!accessToken || !refreshToken) {
                setApplicationError("로그인이 필요합니다.");
                return;
            }

            const hasCert = !!form.auctionAgentCertFileUrl;
            const expertBody: Omit<ExpertApplicationInfo, "phone"> = {
                ...form,
                profile: form.profileFileUrl || "",
                career: form.career || initializeCareer(form.counselingCategories),
                isAuctionAgent: form.counselingCategories.includes("매수신청대리인") && !!hasCert,
                auctionAgentCertFileUrl: form.auctionAgentCertFileUrl ?? "",
            };

            await registerExpertInfo(expertBody);

            if (form.counselingCategories.includes("매수신청대리인")) {
                setShowPendingPopup(true);
            } else {
                transferToNative(JSON.stringify({ accessToken, refreshToken }), "onRedirectHomeWithToken");
                transferToNative("done", "closeWebView");
            }
        } catch (err: any) {
            setApplicationError(err.message || "신청에 실패했습니다.");
        }
    };

    const handleCancel = () => {
        transferToNative("done", "closeWebView");
        navigate(-1);
    };

    useEffect(() => {
        validateAndSubmitForm("validate");
    }, [form]);

    useEffect(() => {
        const fetchPhone = async () => {
            try {
                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) throw new Error("토큰이 없습니다.");

                const phone = await getUserPhone();

                setPartialState({ form: { ...form, phone } });
            } catch (err) {
                console.error("휴대폰 번호 조회 실패", err);
            } finally {
                setLoadingPhone(false);
            }
        };

        fetchPhone();
    }, []);

    return (
        <Background>
            <Expert_Container>
                <TitleBox>
                    <TitleHeader>
                        <img className="logo" alt="미스고 통합 로고" src={missgoUnifiedLogo} width={80} height={36} />
                        <div onClick={handleCancel} style={{ cursor: "pointer" }}>
                            <Cancel />
                        </div>
                    </TitleHeader>
                    <SubTitleStyle>
                        <h3 className="title">전문가 신청</h3>
                    </SubTitleStyle>
                </TitleBox>

                {/* 프로필 사진 */}
                <InputHeader>
                    <div className="star">*</div>
                    <div className="title">프로필 사진</div>
                </InputHeader>
                <InputBox className="profile-box">
                    <SignupProfileUploadContainer />
                </InputBox>

                {/* 원하는 전문가 */}
                <InputBox>
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">원하는 전문가 (복수 선택 가능)</div>
                    </InputHeader>
                    <ExpertSelect />
                </InputBox>

                {/* 계좌번호 정보 */}
                <InputBox>
                    <InputHeader>
                        <div className="star">*</div>
                        <div className="title">계좌번호 정보</div>
                    </InputHeader>
                    <MgInput
                        width="100%"
                        value={form.bankName}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("bankName", e)}
                        placeholder="은행명"
                        noBorder
                        bgColor="#FBF8F8"
                    />
                    <MgInput
                        width="100%"
                        value={form.bankAccount}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange("bankAccount", e)}
                        placeholder="계좌번호 입력"
                        noBorder
                        bgColor="#FBF8F8"
                        inputMode="numeric"
                    />
                </InputBox>

                {/* 경력 연도 */}
                {form.counselingCategories.length > 0 && (
                    <InputBox>
                        <InputHeader>
                            <div className="star">*</div>
                            <div className="title">경력 (시작 연도 입력)</div>
                        </InputHeader>
                        {COUNCELING_CATEGORIES_ORDER.filter((category) => form.counselingCategories.includes(category)).map(
                            (category) => (
                                <YearRow key={category}>
                                    <YearLabel>{category === "매수신청대리인" ? "경매대행" : category}</YearLabel>
                                    <YearInputWrap>
                                        <MgInput
                                            width="100%"
                                            maxLength={4}
                                            inputMode="numeric"
                                            value={form.career?.[category] || ""}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const raw = e.target.value ? Number(e.target.value) : 0;
                                                const currentYear = new Date().getFullYear();
                                                const value = raw >= 1000 && raw > currentYear ? currentYear : raw;
                                                const currentCareer = form.career || initializeCareer(form.counselingCategories);
                                                setPartialState({
                                                    form: {
                                                        ...form,
                                                        career: {
                                                            ...currentCareer,
                                                            [category]: value,
                                                        } as Record<CounselingCategory, number>,
                                                    },
                                                });
                                            }}
                                            placeholder="YYYY"
                                            noBorder
                                            bgColor="#FBF8F8"
                                        />

                                        <YearSuffix>년</YearSuffix>
                                    </YearInputWrap>
                                </YearRow>
                            ),
                        )}
                    </InputBox>
                )}

                {/* 자격 */}
                <InputBox>
                    <InputHeader>
                        <div className="title">자격 (선택)</div>
                    </InputHeader>
                    <MgInput
                        as="textarea"
                        width="100%"
                        value={form.qualification}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setPartialState({ form: { ...form, qualification: e.target.value } })
                        }
                        placeholder="다른 자격 사항이 있으면 작성해주세요."
                        noBorder
                        bgColor="#FBF8F8"
                        height="170px"
                        maxLength={200}
                    />
                </InputBox>

                {/* 소개 */}
                <InputBox>
                    <InputHeader>
                        <div className="title">소개 (선택)</div>
                    </InputHeader>
                    <MgInput
                        as="textarea"
                        width="100%"
                        value={form.introduction}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                            setPartialState({ form: { ...form, introduction: e.target.value } })
                        }
                        placeholder="소개 글을 작성해주세요."
                        noBorder
                        bgColor="#FBF8F8"
                        height="170px"
                        maxLength={500}
                    />
                </InputBox>

                {/* 신청 완료 버튼 */}
                <BtnStyle className={canSubmit ? "active" : ""} onClick={() => validateAndSubmitForm("submit")}>
                    전문가 신청
                </BtnStyle>

                {applicationError && (
                    <MgDialog
                        addDialogStyle={{ zIndex: 5000 }}
                        open={true}
                        onCloseView={() => setApplicationError("")}
                        contents={
                            <div style={{ textAlign: "center", padding: "24px 0", width: "240px" }}>{applicationError}</div>
                        }
                        confirmText="확인"
                        confirmStyle={{ fontSize: 16 }}
                        onClickSubmit={() => setApplicationError("")}
                    />
                )}

                <MgDialog
                    open={showPendingPopup}
                    onCloseView={handlePendingPopupConfirm}
                    contents={
                        <div style={{ textAlign: "center", padding: "24px 0", width: "240px" }}>
                            <p style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>관리자 승인 대기중</p>
                            <img
                                src={WaitConfirm}
                                alt="승인 대기중"
                                style={{ width: 140, height: 140, objectFit: "contain", margin: "8px 0 12px" }}
                            />
                            <p style={{ fontSize: 14, color: "#6b7685", marginTop: 12 }}>
                                매수신청대리인의 경우
                                <br />
                                관리자의 별도 승인이 필요합니다.
                                <br />
                                승인 후 경매대행을 할 수 있습니다.
                            </p>
                        </div>
                    }
                    confirmText="확인"
                />
            </Expert_Container>
        </Background>
    );
};

export default ExpertApplication;

const Background = styled.div`
    position: relative;
    width: 100%;
    height: 100vh;
    background-color: #f5f5f5;
    background-position: center;
    overflow: hidden;

    @media screen and (max-width: 768px) {
        background-image: none;
        background-color: #ffffff;
    }
`;

const Expert_Container = styled.div`
    display: flex;
    height: 100vh;
    flex-direction: column;
    padding: 24px;
    overflow-y: scroll;
    background: #ffffff;

    .subTitle {
        font-size: 12px;
        font-weight: 400;
        color: #b8b8b8;
        line-height: 140%;
        margin-bottom: 8px;
    }

    @media screen and (min-width: 768px) {
        position: absolute;
        width: 336px;
        height: 700px;
        top: 60px;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 60px;
        border-radius: 12px;
        background: #ffffff;
        overflow-y: auto;
        /* 내부 요소 스크롤, 위아래 공간 */
    }
`;

const TitleBox = styled.div`
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;
`;

const TitleHeader = styled.div`
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
`;

const SubTitleStyle = styled.div`
    color: #222222;
    font-size: 24px;
    font-weight: 700;
    line-height: 140%;
    @media screen and (max-width: 768px) {
        margin-top: 0px;
        margin-bottom: 0px;
    }
`;

const InputHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;

    .star {
        color: ${Common.colors.notiColor};
    }

    .title {
        color: ${Common.colors.normalTextColor};
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
    }
`;

const InputBox = styled.div`
    margin-bottom: 24px;

    &.profile-box {
        display: flex;
        justify-content: center;
    }

    & > input + input {
        margin-top: 8px;
    }
`;

const YearRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 0;
`;

const YearLabel = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #0c002c;
`;

const YearInputWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100px;
`;

const YearSuffix = styled.span`
    font-size: 16px;
    color: #140c2c;
`;

const BtnStyle = styled.button`
    padding: 12px 14px;
    width: 100%;
    border-radius: 12px;
    background: #c3c3c3;
    color: #ffffff;
    font-weight: 400;
    font-size: 18px;
    cursor: pointer;

    &.active {
        background: ${Common.colors.aucMarkerColor};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }

    @media screen and (max-width: 768px) {
        width: 100%;
    }
`;

const PendingPopupContents = styled.div`
    width: 320px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .title {
        font-size: 22px;
        font-weight: 800;
        line-height: 140%;
        color: ${Common.colors.navyPrimary};
        margin-bottom: 12px;
        text-align: center;
    }

    .img {
        width: 140px;
        height: 140px;
        object-fit: contain;
        margin: 8px 0 12px;
    }

    .desc {
        font-size: 16px;
        font-weight: 400;
        line-height: 150%;
        color: #6b7685;
        text-align: center;
    }
`;

const CheckPartnerCodeButton = styled.button`
    color: ${Common.colors.blue};
    text-decoration: underline;
    padding: 5px 8px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 140%;
    font-weight: 400;
    margin-left: auto;
    white-space: nowrap;
    background: transparent;
    border: none;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        text-decoration: none;
    }
`;

const SubTitle = styled.p`
    font-size: 12px;
    font-weight: 400;
    color: #9da6b3;
    line-height: 140%;
    margin: 8px 0 8px;
`;

const PartnerCodeMsg = styled.p`
    margin-top: 6px;
    font-size: 12px;
    line-height: 140%;

    &[data-valid="yes"] {
        color: #2275ee;
    }
    &[data-valid="no"] {
        color: ${Common.colors.notiColor};
    }
`;

//필수 필드 key
const KEY_TO_KR: Partial<Record<keyof SignupExpertParams, string>> = {
    bankName: "은행명을 입력해주세요",
    bankAccount: "계좌번호를 입력해주세요",
    counselingCategories: "원하는 전문가를 선택해주세요",
};

export const COUNCELING_CATEGORIES_ORDER: CounselingCategory[] = ["중개", "매물컨설팅", "매수신청대리인", "임장동행"];
